import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BroadcastIcon, FlaskIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import {
  CHANNEL_KINDS,
  SEVERITIES,
  type ChannelKind,
  type LabelMatcher,
  type Route,
  type Severity,
} from '@meshalert/contract';
import { ApiError, api } from '../lib/api.ts';
import { CHANNEL_LABEL, SEVERITY_LABEL } from '../lib/format.ts';
import { PageHeader } from '../components/Layout.tsx';
import { useSession } from '../lib/session.tsx';
import { Dialog } from '../components/Dialog.tsx';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ListSkeleton,
  Panel,
  Select,
  Tag,
  Textarea,
  cx,
} from '../components/ui.tsx';

/**
 * Routing rules decide who gets paged. Editing them blind is how people miss
 * outages, so the editor sits next to a dry run that answers "who would this
 * reach" without sending anything.
 */
export function RoutesPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Route | 'new' | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const routes = useQuery({ queryKey: ['routes'], queryFn: () => api.routes.list({ limit: 100 }) });

  const remove = useMutation({
    mutationFn: (id: string) => api.routes.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['routes'] }),
  });

  const mayEdit = can('admin', 'operator');

  return (
    <>
      <PageHeader
        title="Weiterleitung"
        description="Regeln werden von oben nach unten geprüft. Ein Kanal wird pro Alarm nur einmal bedient, und zwar von der ersten Regel, die ihn erreicht."
        action={
          <div className="flex gap-2">
            <Button icon={<FlaskIcon size={15} />} onClick={() => setPreviewing(true)}>
              Probelauf
            </Button>
            {mayEdit ? (
              <Button variant="primary" icon={<PlusIcon size={15} />} onClick={() => setEditing('new')}>
                Regel anlegen
              </Button>
            ) : null}
          </div>
        }
      />

      {routes.isLoading ? (
        <Panel bodyClassName="p-0">
          <ListSkeleton rows={3} />
        </Panel>
      ) : routes.isError ? (
        <Panel>
          <ErrorState message="Die Regeln konnten nicht geladen werden." onRetry={() => routes.refetch()} />
        </Panel>
      ) : routes.data && routes.data.items.length > 0 ? (
        <ol className="space-y-3">
          {routes.data.items.map((route, index) => (
            <li key={route.id}>
              <Panel bodyClassName="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="tnum rounded-sm bg-sunken px-1.5 py-0.5 text-[11px] text-ink-3">
                        {index + 1}
                      </span>
                      <h2
                        className={cx(
                          'text-[14px] font-semibold',
                          route.is_active ? 'text-ink' : 'text-ink-3',
                        )}
                      >
                        {route.name}
                      </h2>
                      {!route.is_active ? (
                        <span className="rounded-sm bg-sunken px-1.5 text-[11px] text-ink-3">
                          deaktiviert
                        </span>
                      ) : null}
                      {!route.continue_matching ? (
                        <span className="rounded-sm border border-warning/40 px-1.5 text-[11px] text-warning">
                          stoppt hier
                        </span>
                      ) : null}
                    </div>
                    {route.description ? (
                      <p className="mt-1.5 max-w-[70ch] text-[13px] leading-relaxed text-ink-2">
                        {route.description}
                      </p>
                    ) : null}
                  </div>

                  {mayEdit ? (
                    <div className="flex gap-1.5">
                      <Button size="sm" onClick={() => setEditing(route)}>
                        Bearbeiten
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`${route.name} löschen`}
                        onClick={() => {
                          if (confirm(`Regel "${route.name}" wirklich löschen?`)) remove.mutate(route.id);
                        }}
                      >
                        <TrashIcon size={14} />
                      </Button>
                    </div>
                  ) : null}
                </div>

                <div className="mt-3 grid gap-4 border-t border-line pt-3 sm:grid-cols-2">
                  <div>
                    <p className="mb-1.5 text-[11px] text-ink-3">Greift bei</p>
                    <div className="flex flex-wrap gap-1.5">
                      <Tag>
                        ab {SEVERITY_LABEL[route.match.severity_min]}
                      </Tag>
                      {route.match.source_ids.length > 0 ? (
                        <Tag>{route.match.source_ids.length} Quellen</Tag>
                      ) : (
                        <Tag>alle Quellen</Tag>
                      )}
                      {route.match.title_contains ? (
                        <Tag>Titel enthält {route.match.title_contains}</Tag>
                      ) : null}
                      {route.match.label_matchers.map((matcher, i) => (
                        <Tag key={i}>
                          {matcher.key} {matcher.op === 'eq' ? '=' : matcher.op === 'neq' ? '!=' : '~'}{' '}
                          {matcher.value}
                        </Tag>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="mb-1.5 text-[11px] text-ink-3">Geht an</p>
                    <div className="flex flex-wrap gap-1.5">
                      {route.targets.map((target, i) => (
                        <Tag key={i}>
                          {target.user_name}
                          {target.channel_kind ? ` / ${CHANNEL_LABEL[target.channel_kind]}` : ' / alle Kanäle'}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </div>
              </Panel>
            </li>
          ))}
        </ol>
      ) : (
        <Panel bodyClassName="p-0">
          <EmptyState
            icon={<BroadcastIcon size={26} weight="light" />}
            title="Noch keine Regel"
            description="Ohne Regel wird ein eingegangener Alarm gespeichert, aber an niemanden zugestellt. Eine Auffangregel an dich selbst ist ein guter Anfang."
            action={
              mayEdit ? (
                <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
                  Erste Regel anlegen
                </Button>
              ) : null
            }
          />
        </Panel>
      )}

      {editing ? (
        <RouteDialog
          route={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['routes'] });
          }}
        />
      ) : null}

      {previewing ? <PreviewDialog onClose={() => setPreviewing(false)} /> : null}
    </>
  );
}

function RouteDialog({
  route,
  onClose,
  onSaved,
}: {
  route: Route | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(route?.name ?? '');
  const [description, setDescription] = useState(route?.description ?? '');
  const [priority, setPriority] = useState(route?.priority ?? 100);
  const [isActive, setIsActive] = useState(route?.is_active ?? true);
  const [continueMatching, setContinueMatching] = useState(route?.continue_matching ?? true);
  const [severityMin, setSeverityMin] = useState<Severity>(route?.match.severity_min ?? 'info');
  const [sourceIds, setSourceIds] = useState<string[]>(route?.match.source_ids ?? []);
  const [titleContains, setTitleContains] = useState(route?.match.title_contains ?? '');
  const [matchers, setMatchers] = useState<LabelMatcher[]>(route?.match.label_matchers ?? []);
  const [targets, setTargets] = useState<{ user_id: string; channel_kind: ChannelKind | null }[]>(
    route?.targets.map((t) => ({ user_id: t.user_id, channel_kind: t.channel_kind ?? null })) ?? [],
  );

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users.list({ limit: 100 }) });
  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.sources.list({ limit: 100 }) });

  const body = {
    name,
    description: description || null,
    priority,
    continue_matching: continueMatching,
    match: {
      source_ids: sourceIds,
      severity_min: severityMin,
      label_matchers: matchers.filter((m) => m.key.trim() !== ''),
      title_contains: titleContains || null,
    },
    targets,
  };

  const save = useMutation({
    mutationFn: () =>
      route
        ? api.routes.update(route.id, { ...body, is_active: isActive })
        : api.routes.create(body),
    onSuccess: onSaved,
  });

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const generalError =
    save.error instanceof ApiError && Object.keys(fieldErrors).length === 0 ? save.error.message : null;

  return (
    <Dialog wide title={route ? 'Regel bearbeiten' : 'Regel anlegen'} onClose={onClose}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
          <Field label="Name" error={fieldErrors.name}>
            {(id) => (
              <Input
                id={id}
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Kritisches an die Bereitschaft"
              />
            )}
          </Field>
          <Field label="Reihenfolge" hint="Kleinere Zahl wird zuerst geprüft.">
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                max={10000}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="tnum"
              />
            )}
          </Field>
        </div>

        <Field label="Notiz">
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Warum gibt es diese Regel?"
            />
          )}
        </Field>

        <fieldset className="space-y-4 rounded-md border border-line bg-sunken p-4">
          <legend className="px-1 text-[12px] font-medium text-ink-2">Greift bei</legend>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Ab Schweregrad">
              {(id) => (
                <Select
                  id={id}
                  value={severityMin}
                  onChange={(e) => setSeverityMin(e.target.value as Severity)}
                >
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {SEVERITY_LABEL[s]} und höher
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Titel enthält" hint="Optional, ohne Beachtung der Groß-Schreibung.">
              {(id) => (
                <Input
                  id={id}
                  value={titleContains}
                  onChange={(e) => setTitleContains(e.target.value)}
                  placeholder="backup"
                />
              )}
            </Field>
          </div>

          <Field label="Quellen" hint="Nichts ausgewählt bedeutet: alle Quellen.">
            {() => (
              <div className="flex flex-wrap gap-1.5">
                {(sources.data?.items ?? []).map((source) => {
                  const selected = sourceIds.includes(source.id);
                  return (
                    <button
                      key={source.id}
                      type="button"
                      onClick={() =>
                        setSourceIds((prev) =>
                          selected ? prev.filter((id) => id !== source.id) : [...prev, source.id],
                        )
                      }
                      className={cx(
                        'h-7 rounded-md border px-2.5 text-[12px] transition-colors',
                        selected
                          ? 'border-accent bg-accent-soft text-ink'
                          : 'border-line-strong bg-raised text-ink-2 hover:bg-hover',
                      )}
                    >
                      {source.name}
                    </button>
                  );
                })}
              </div>
            )}
          </Field>

          <div>
            <p className="mb-1.5 text-[13px] font-medium text-ink">Label-Bedingungen</p>
            <p className="mb-2 -mt-1 text-[12px] text-ink-3">
              Alle müssen zutreffen. Ohne Bedingung greift die Regel für jedes Label.
            </p>
            <div className="space-y-2">
              {matchers.map((matcher, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={matcher.key}
                    onChange={(e) =>
                      setMatchers((prev) =>
                        prev.map((m, i) => (i === index ? { ...m, key: e.target.value } : m)),
                      )
                    }
                    placeholder="component"
                    aria-label="Label"
                    className="flex-1 font-mono"
                  />
                  <Select
                    value={matcher.op}
                    onChange={(e) =>
                      setMatchers((prev) =>
                        prev.map((m, i) =>
                          i === index ? { ...m, op: e.target.value as LabelMatcher['op'] } : m,
                        ),
                      )
                    }
                    aria-label="Vergleich"
                    className="w-28"
                  >
                    <option value="eq">ist</option>
                    <option value="neq">ist nicht</option>
                    <option value="matches">passt auf</option>
                  </Select>
                  <Input
                    value={matcher.value}
                    onChange={(e) =>
                      setMatchers((prev) =>
                        prev.map((m, i) => (i === index ? { ...m, value: e.target.value } : m)),
                      )
                    }
                    placeholder="mesh"
                    aria-label="Wert"
                    className="flex-1 font-mono"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    aria-label="Bedingung entfernen"
                    onClick={() => setMatchers((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <TrashIcon size={14} />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                onClick={() => setMatchers((prev) => [...prev, { key: '', op: 'eq', value: '' }])}
              >
                Bedingung hinzufügen
              </Button>
            </div>
          </div>
        </fieldset>

        <fieldset className="space-y-3 rounded-md border border-line bg-sunken p-4">
          <legend className="px-1 text-[12px] font-medium text-ink-2">Geht an</legend>

          {targets.length === 0 ? (
            <p className="text-[12px] text-ink-3">Noch kein Ziel. Ohne Ziel kann die Regel nichts tun.</p>
          ) : null}

          <div className="space-y-2">
            {targets.map((target, index) => (
              <div key={index} className="flex gap-2">
                <Select
                  value={target.user_id}
                  onChange={(e) =>
                    setTargets((prev) =>
                      prev.map((t, i) => (i === index ? { ...t, user_id: e.target.value } : t)),
                    )
                  }
                  aria-label="Empfänger"
                  className="flex-1"
                >
                  {(users.data?.items ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
                <Select
                  value={target.channel_kind ?? ''}
                  onChange={(e) =>
                    setTargets((prev) =>
                      prev.map((t, i) =>
                        i === index
                          ? { ...t, channel_kind: (e.target.value || null) as ChannelKind | null }
                          : t,
                      ),
                    )
                  }
                  aria-label="Kanalart"
                  className="w-44"
                >
                  <option value="">alle Kanäle</option>
                  {CHANNEL_KINDS.map((k) => (
                    <option key={k} value={k}>
                      nur {CHANNEL_LABEL[k]}
                    </option>
                  ))}
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  aria-label="Ziel entfernen"
                  onClick={() => setTargets((prev) => prev.filter((_, i) => i !== index))}
                >
                  <TrashIcon size={14} />
                </Button>
              </div>
            ))}
          </div>

          <Button
            type="button"
            size="sm"
            disabled={(users.data?.items.length ?? 0) === 0}
            onClick={() =>
              setTargets((prev) => [
                ...prev,
                { user_id: users.data?.items[0]?.id ?? '', channel_kind: null },
              ])
            }
          >
            Ziel hinzufügen
          </Button>
        </fieldset>

        <div className="space-y-2.5">
          <Checkbox
            label="Weitere Regeln danach prüfen"
            description="Ausschalten, wenn diese Regel die letzte Instanz sein soll."
            checked={continueMatching}
            onChange={(e) => setContinueMatching(e.target.checked)}
          />
          {route ? (
            <Checkbox
              label="Regel aktiv"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          ) : null}
        </div>

        {generalError ? (
          <p role="alert" className="text-[13px] text-critical">
            {generalError}
          </p>
        ) : null}
        {fieldErrors.targets ? (
          <p role="alert" className="text-[13px] text-critical">
            {fieldErrors.targets}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={save.isPending || targets.length === 0}>
            {save.isPending ? 'Wird gespeichert' : 'Speichern'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function PreviewDialog({ onClose }: { onClose: () => void }) {
  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.sources.list({ limit: 100 }) });
  const [sourceId, setSourceId] = useState('');
  const [severity, setSeverity] = useState<Severity>('critical');
  const [title, setTitle] = useState('Testalarm');
  const [labelText, setLabelText] = useState('');

  const effectiveSource = sourceId || sources.data?.items[0]?.id || '';

  const preview = useMutation({
    mutationFn: () =>
      api.routes.preview({
        source_id: effectiveSource,
        severity,
        title,
        labels: parseLabels(labelText),
      }),
  });

  return (
    <Dialog wide title="Probelauf" onClose={onClose}>
      <p className="mb-4 text-[13px] leading-relaxed text-ink-2">
        Zeigt, welche Regeln greifen und welche Geräte erreicht würden. Es wird nichts gesendet.
      </p>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          preview.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Quelle">
            {(id) => (
              <Select id={id} value={effectiveSource} onChange={(e) => setSourceId(e.target.value)}>
                {(sources.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Schweregrad">
            {(id) => (
              <Select
                id={id}
                value={severity}
                onChange={(e) => setSeverity(e.target.value as Severity)}
              >
                {[...SEVERITIES].reverse().map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_LABEL[s]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field label="Titel">
          {(id) => <Input id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>

        <Field label="Labels" hint="Ein Paar pro Zeile, im Format schlüssel=wert.">
          {(id) => (
            <Textarea
              id={id}
              rows={3}
              value={labelText}
              onChange={(e) => setLabelText(e.target.value)}
              placeholder={'component=mesh\nregion=b'}
              className="font-mono"
            />
          )}
        </Field>

        <Button type="submit" variant="primary" disabled={preview.isPending || !effectiveSource}>
          {preview.isPending ? 'Wird geprüft' : 'Probelauf starten'}
        </Button>
      </form>

      {preview.data ? (
        <div className="mt-5 space-y-4 border-t border-line pt-4">
          <div>
            <p className="mb-1.5 text-[12px] font-medium text-ink-2">Greifende Regeln</p>
            {preview.data.matched_routes.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {preview.data.matched_routes.map((r) => (
                  <Tag key={r.id}>{r.name}</Tag>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-ink-3">Keine Regel greift bei diesem Alarm.</p>
            )}
          </div>

          <div>
            <p className="mb-1.5 text-[12px] font-medium text-ink-2">Erreichte Kanäle</p>
            {preview.data.channels.length > 0 ? (
              <ul className="space-y-1.5">
                {preview.data.channels.map((c) => (
                  <li
                    key={c.channel_id}
                    className="flex items-center justify-between gap-3 rounded-md border border-line bg-sunken px-3 py-2 text-[13px]"
                  >
                    <span className="text-ink">
                      {c.label} <span className="text-ink-3">- {c.user_name}</span>
                    </span>
                    <span className="text-[12px] text-ink-3">über {c.via_route}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-critical">
                Niemand würde benachrichtigt. Prüf die Regeln und ob die Zielpersonen aktive Kanäle
                haben.
              </p>
            )}
          </div>
        </div>
      ) : null}

      {preview.error ? (
        <p role="alert" className="mt-4 text-[13px] text-critical">
          {preview.error instanceof ApiError ? preview.error.message : 'Der Probelauf ist fehlgeschlagen.'}
        </p>
      ) : null}
    </Dialog>
  );
}

function parseLabels(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const separator = line.indexOf('=');
    if (separator <= 0) continue;
    const key = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    if (key) out[key] = value;
  }
  return out;
}
